import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FileIcon from '@mui/icons-material/InsertDriveFile';
import MapIcon from '@mui/icons-material/Map';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import { GridColDef } from '@mui/x-data-grid';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import DashboardDataGrid from 'src/common/components/dataGrid/DashboardDataGrid';
import { handymanActionsColumn } from 'src/common/components/dataGrid/dashboardGridColumns';
import { UploadedFile } from 'src/interfaces/FileManager';

import LightboxImageTrigger from 'src/common/components/LightboxImageTrigger';

import { FileRowActionsMenu } from './fileTableCells';
import CopyFileLinkButton from './CopyFileLinkButton';

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

const endpointLabelKey = {
  image: 'system.fileManager.types.image',
  document: 'system.fileManager.types.document',
  trackMap: 'system.fileManager.types.trackMap',
} as const;

const FilePreviewCell = ({ file }: { file: UploadedFile }) => {
  if (file.uploadEndpoint === 'image') {
    return (
      <LightboxImageTrigger
        src={file.fileUrl}
        alt={file.altText || file.fileName}
        stopPropagation
        sx={{ width: 48, height: 48, objectFit: 'contain', display: 'block' }}
      />
    );
  }
  const Icon = file.uploadEndpoint === 'trackMap' ? MapIcon : FileIcon;
  return <Icon sx={{ fontSize: 40, color: 'grey.500' }} />;
};

interface FilesTableProps {
  files: UploadedFile[];
  mode: 'manage' | 'select';
  multiple?: boolean;
  selectedFileIds?: string[];
  onSelect: (file: UploadedFile) => void;
  onDelete: (fileId: string) => void;
  onEdit: (file: UploadedFile) => void;
}

const FilesTable = ({
  files,
  mode,
  multiple = false,
  selectedFileIds = [],
  onSelect,
  onDelete,
  onEdit,
}: FilesTableProps) => {
  const { t } = useTranslation();
  const isMultiSelect = mode === 'select' && multiple;

  const columns = useMemo((): GridColDef<UploadedFile>[] => {
    const base: GridColDef<UploadedFile>[] = [];

    if (isMultiSelect) {
      base.push({
        field: 'select',
        headerName: '',
        width: 52,
        sortable: false,
        renderCell: ({ row }) => (
          <Checkbox
            checked={selectedFileIds.includes(row.id)}
            onChange={() => onSelect(row)}
            slotProps={{ input: { 'aria-label': t('editor.iconSelector.select') } }}
          />
        ),
      });
    }

    base.push(
      {
        field: 'preview',
        headerName: '',
        width: 72,
        sortable: false,
        renderCell: ({ row }) => <FilePreviewCell file={row} />,
      },
      {
        field: 'fileName',
        headerName: t('system.fileManager.editMetadata.fileName'),
        flex: 1,
        minWidth: 160,
        sortable: false,
      },
      {
        field: 'fileType',
        headerName: t('system.fileManager.fileType'),
        width: 120,
        sortable: false,
        valueGetter: (_, row) => t(endpointLabelKey[row.uploadEndpoint]),
      },
      {
        field: 'fileSize',
        headerName: t('system.fileManager.sort.size'),
        width: 100,
        sortable: false,
        valueGetter: (_, row) => formatBytes(row.fileSize),
      },
      {
        field: 'usage',
        headerName: t('system.fileManager.usageStatus'),
        width: 100,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            label={t(row.isUsed ? 'system.fileManager.used' : 'system.fileManager.unused')}
            color={row.isUsed ? 'success' : 'default'}
            size="small"
          />
        ),
      },
    );

    if (mode === 'select') {
      base.push(
        handymanActionsColumn<UploadedFile>(
          t,
          'technicalTypes.optionsLabel',
          ({ row }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CopyFileLinkButton fileUrl={row.fileUrl} />
              {!isMultiSelect ? (
                <Button size="small" variant="outlined" onClick={() => onSelect(row)}>
                  {t('editor.iconSelector.select')}
                </Button>
              ) : null}
              <IconButton
                size="small"
                color="error"
                aria-label={t('base.delete')}
                onClick={() => onDelete(row.id)}
              >
                <DeleteForeverIcon fontSize="small" />
              </IconButton>
            </Box>
          ),
          isMultiSelect ? 96 : 160,
        ),
      );
    } else if (mode === 'manage') {
      base.push(
        handymanActionsColumn<UploadedFile>(t, 'technicalTypes.optionsLabel', ({ row }) => (
          <FileRowActionsMenu file={row} onEdit={onEdit} onDelete={onDelete} />
        )),
      );
    }

    return base;
  }, [isMultiSelect, mode, onDelete, onEdit, onSelect, selectedFileIds, t]);

  return (
    <DashboardDataGrid<UploadedFile>
      aria-label={t('system.fileManager.pageTitle')}
      chrome="compact"
      rows={files}
      columns={columns}
    />
  );
};

export default FilesTable;
